import { useCallback, useEffect, useState } from 'react';
import { ArrowLeftIcon, ArrowRightIcon } from '@icons';
import { TempProjectDetailsMediaImage } from '@images';
import { initiativeImageBinaryUrl } from '@utils';
import {
  CardCarousel,
  CardCarouselButton,
  CardCarouselImage,
  CardCarouselPlaceholder,
} from './TableCard.style';

type CardImageCarouselProps = {
  initiativeId: string;
  images: string[];
  alt: string;
};

export function CardImageCarousel({ initiativeId, images, alt }: CardImageCarouselProps) {
  const [index, setIndex] = useState(0);
  const count = images.length;
  const safeIndex = count > 0 ? Math.min(index, count - 1) : 0;

  useEffect(() => {
    setIndex((current) => (count === 0 ? 0 : Math.min(current, count - 1)));
  }, [count]);

  const goPrev = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      if (count <= 1) return;
      setIndex((current) => Math.max(0, current - 1));
    },
    [count]
  );

  const goNext = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      if (count <= 1) return;
      setIndex((current) => Math.min(count - 1, current + 1));
    },
    [count]
  );

  if (count === 0) {
    return (
      <CardCarousel>
        <CardCarouselPlaceholder src={TempProjectDetailsMediaImage} alt={alt} />
      </CardCarousel>
    );
  }

  const currentImage = images[safeIndex];
  const isFirst = safeIndex <= 0;
  const isLast = safeIndex >= count - 1;

  return (
    <CardCarousel
      role="region"
      aria-roledescription="carrossel"
      aria-label="Imagens do projeto"
      onClick={(event) => event.stopPropagation()}
    >
      <CardCarouselImage
        key={currentImage}
        src={initiativeImageBinaryUrl(initiativeId, currentImage)}
        alt={alt}
      />

      {count > 1 && !isFirst ? (
        <CardCarouselButton type="button" $side="left" onClick={goPrev} aria-label="Imagem anterior">
          <ArrowLeftIcon aria-hidden />
        </CardCarouselButton>
      ) : null}

      {count > 1 && !isLast ? (
        <CardCarouselButton type="button" $side="right" onClick={goNext} aria-label="Próxima imagem">
          <ArrowRightIcon aria-hidden />
        </CardCarouselButton>
      ) : null}
    </CardCarousel>
  );
}
